import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, ExternalLink, Download } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface ProductivityImproverProps {
  onClose: () => void;
}

export default function ProductivityImprover({ onClose }: ProductivityImproverProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please describe your productivity challenges');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Create a detailed productivity improvement plan for the following situation:

${input}

Format your response as follows:

<h2>Summary:</h2>
[Provide a brief summary of the key challenges identified from the input]

<h2>Key Challenges Identified:</h2>
<ul>
<li>[Challenge 1]</li>
<li>[Challenge 2]</li>
<li>[Challenge 3]</li>
<li>[Challenge 4]</li>
</ul>

<h2>Suggested Action Plan:</h2>

<h3>1. [First Strategy Category]:</h3>
[Detailed explanation with specific actionable steps]

<h3>2. [Second Strategy Category]:</h3>
[Detailed explanation with specific actionable steps]

<h3>3. [Third Strategy Category]:</h3>
[Detailed explanation with specific actionable steps]

<h3>4. [Fourth Strategy Category]:</h3>
[Detailed explanation with specific actionable steps]

<h3>5. [Fifth Strategy Category]:</h3>
[Detailed explanation with specific actionable steps]

Use HTML formatting for better readability. Focus on practical, actionable recommendations that address the specific productivity challenges mentioned. Include time management techniques, focus strategies, habit-building approaches, and tools/systems that could help.`;

      const response = await generateTaskDescription(prompt, 'task');
      setResult(response);
      setPlanGenerated(true);
      toast.success('Productivity plan generated successfully');

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating productivity plan:', error);
      toast.error('Failed to generate productivity plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }

      // First try to find the agent by name
      const { data: existingAgent, error: findError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('name', 'Productivity Improvement Planner')
        .limit(1)
        .single();

      if (findError && findError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error finding agent:', findError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }

      let agentId: string;

      if (!existingAgent) {
        // Create new agent with a generated UUID
        const { data: newAgent, error: createError } = await supabase
          .from('ai_agents')
          .insert({
            user_id: user.id,
            name: 'Productivity Improvement Planner',
            goals: 'Help users improve their productivity and time management',
            system_prompt: 'You are a productivity improvement expert that helps users create actionable plans to improve their productivity.',
            is_custom: false,
            agent_type: 'productivity'
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating agent:', createError);
          toast.error('Failed to create productivity agent');
          setSavingOutput(false);
          return;
        }

        agentId = newAgent.id;
      } else {
        agentId = existingAgent.id;
      }

      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });

      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      // Create a temporary element to properly strip HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = result || '';
      const textContent = tempElement.textContent || tempElement.innerText || '';
      
      await navigator.clipboard.writeText(textContent);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header with title
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Productivity Improvement Plan';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Parse HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');
      
      // Start position after header
      let y = 50;
      
      // Add original input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Your Situation:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Process each heading and its content
      const headings = doc.querySelectorAll('h2, h3');
      headings.forEach((heading) => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = 20;
        }

        // Set heading font
        pdf.setFontSize(heading.tagName === 'H2' ? 16 : 14);
        pdf.setFont('helvetica', 'bold');
        
        // Add background for headings
        if (heading.textContent?.includes('Summary') || heading.textContent?.includes('Challenges') || heading.textContent?.includes('Action Plan')) {
          pdf.setFillColor(240, 247, 255); // Light blue background
          pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        }
        
        pdf.text(heading.textContent || '', margin, y);
        y += 10;
        
        // Process content until next heading
        let nextElement = heading.nextElementSibling;
        while (nextElement && !['H2', 'H3'].includes(nextElement.tagName)) {
          // Check if we need a new page
          if (y > pdf.internal.pageSize.height - 20) {
            pdf.addPage();
            y = 20;
          }
          
          if (nextElement.tagName === 'UL') {
            // Process list items
            const items = nextElement.querySelectorAll('li');
            items.forEach((item) => {
              pdf.setFontSize(11);
              pdf.setFont('helvetica', 'normal');
              pdf.text('•', margin, y);
              const wrappedItem = pdf.splitTextToSize(item.textContent || '', pageWidth - (margin * 2) - 10);
              pdf.text(wrappedItem, margin + 10, y);
              y += wrappedItem.length * 7;
            });
          } else if (nextElement.tagName === 'P') {
            // Process paragraphs
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            const wrappedText = pdf.splitTextToSize(nextElement.textContent || '', pageWidth - (margin * 2));
            pdf.text(wrappedText, margin, y);
            y += wrappedText.length * 7;
          }
          
          nextElement = nextElement.nextElementSibling;
          y += 5; // Add some space between elements
        }
        
        y += 5; // Add space after section
      });
      
      // Add footer with date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, margin, pdf.internal.pageSize.height - 10);
      
      pdf.save('productivity-improvement-plan.pdf');
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  const downloadExamplePDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Productivity Improvement Plan';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Start position after header
      let y = 50;
      
      // Example input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Your Situation:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = "I'm juggling client work, my online course, and trying to stay consistent with gym. I keep switching between tasks and end up doing nothing properly. I mostly work from home but get distracted a lot. I'm trying to build my personal brand too but can't find time to post regularly.";
      const wrappedInput = pdf.splitTextToSize(exampleInput, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Summary section
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255); // Light blue background
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Summary:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const summary = "You are balancing several priorities—client work, online learning, fitness, and personal branding—but task-switching and a distracting home environment are affecting your productivity and consistency.";
      const wrappedSummary = pdf.splitTextToSize(summary, pageWidth - (margin * 2));
      pdf.text(wrappedSummary, margin, y);
      y += wrappedSummary.length * 7 + 15;
      
      // Challenges section
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Key Challenges Identified:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const challenges = [
        "Too many competing priorities",
        "Lack of a structured daily routine",
        "Frequent distractions while working from home",
        "Inconsistent posting for personal brand development"
      ];
      
      challenges.forEach(challenge => {
        pdf.text('•', margin, y);
        const wrappedChallenge = pdf.splitTextToSize(challenge, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedChallenge, margin + 10, y);
        y += wrappedChallenge.length * 7;
      });
      
      y += 15;
      
      // Action Plan section
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Suggested Action Plan:', margin, y);
      y += 15;
      
      // Add a new page for the action plan
      pdf.addPage();
      y = 20;
      
      // Daily Theme Strategy
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('1. Daily Theme Strategy (Focus Days):', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const themes = [
        "Monday/Tuesday: Focus on client work",
        "Wednesday: Dedicate time to your online course",
        "Thursday: Work on content for your personal brand",
        "Friday: Use for admin tasks, catch-up work, and networking"
      ];
      
      themes.forEach(theme => {
        pdf.text('•', margin, y);
        const wrappedTheme = pdf.splitTextToSize(theme, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedTheme, margin + 10, y);
        y += wrappedTheme.length * 7;
      });
      
      y += 15;
      
      // Daily Time Allocation
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('2. Daily Time Allocation:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const timeAllocation = [
        "9:00 AM – 12:00 PM: Deep focus tasks (turn off phone and notifications)",
        "12:00 PM – 1:00 PM: Lunch break and gym preparation",
        "1:00 PM – 2:00 PM: Gym or physical activity",
        "2:00 PM – 4:00 PM: Course practice or content creation",
        "4:00 PM – 5:00 PM: Email, admin, planning the next day"
      ];
      
      timeAllocation.forEach(time => {
        pdf.text('•', margin, y);
        const wrappedTime = pdf.splitTextToSize(time, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedTime, margin + 10, y);
        y += wrappedTime.length * 7;
      });
      
      y += 15;
      
      // Personal Branding Consistency Plan
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('3. Personal Branding Consistency Plan:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const brandingPlan = [
        "Select one content topic per week",
        "Write and schedule two posts on Sunday",
        "Allocate 30 minutes on Thursday to engage with your audience"
      ];
      
      brandingPlan.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 10, y);
        y += wrappedItem.length * 7;
      });
      
      y += 15;
      
      // Focus and Distraction Management
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('4. Focus and Distraction Management:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const focusManagement = [
        "Create a separate, dedicated workspace at home",
        "Use distraction-blocking tools such as Forest or Freedom",
        "Practice single-tasking and batch similar tasks together"
      ];
      
      focusManagement.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 10, y);
        y += wrappedItem.length * 7;
      });
      
      y += 15;
      
      // Motivation and Habit Tracking
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('5. Motivation and Habit Tracking:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const habitTracking = [
        "Use a visual habit tracker for your gym sessions and content posting",
        "Celebrate small wins weekly with meaningful rewards, such as a break or downtime"
      ];
      
      habitTracking.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 10, y);
        y += wrappedItem.length * 7;
      });
      
      // Add footer with date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated by PM Pro AI`, margin, pdf.internal.pageSize.height - 10);
      
      pdf.save('productivity-improvement-plan-example.pdf');
    } catch (error) {
      console.error('Error creating example PDF:', error);
      toast.error('Failed to create example PDF');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Productivity Improvement Planner
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Describe your current productivity challenges, work environment, and goals. The AI will analyze your situation and create a personalized productivity improvement plan with actionable strategies.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={downloadExamplePDF}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="productivity-challenges" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Your Productivity Challenges
              </label>
              <textarea
                id="productivity-challenges"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Describe your current productivity challenges, work environment, and goals..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Plan...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Plan
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Your Productivity Plan
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              {planGenerated && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                    Productivity Plan Generated Successfully
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}